import { useEffect, useRef } from "react";
import { API } from "./api";

// Anonymous, policy-consistent (strictly-necessary) analytics. A random visitor id is stored in
// localStorage — no PII, no marketing use. Beacons are best-effort and never block the UI.
const VID_KEY = "fop_vid";

export function getVisitorId() {
  try {
    let v = localStorage.getItem(VID_KEY);
    if (!v) {
      v = (crypto.randomUUID && crypto.randomUUID()) || `v-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(VID_KEY, v);
    }
    return v;
  } catch {
    return "anon";
  }
}

function beacon(path, body) {
  try {
    const url = `${API}${path}`;
    const payload = JSON.stringify(body);
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
      return;
    }
    fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true }).catch(() => {});
  } catch { /* no-op */ }
}

export function trackPage(path) {
  beacon("/track", { path, visitor_id: getVisitorId(), kind: "page" });
}

export function trackDemo(app, lang, event) {
  beacon("/track/demo", { app, lang, event, visitor_id: getVisitorId() });
}

// Fires "play" once per (app,lang) session, then "midway" at ≥25% progress and "complete" at
// ≥75% progress (2b threshold). play is always recorded before midway/complete so the server's
// bucket math (completed / midway / fainting starts) can never go negative.
export function useDemoTracking(app, lang, stepIndex, total) {
  const started = useRef(false);
  const mid = useRef(false);
  const done = useRef(false);
  const ensurePlay = () => {
    if (!started.current) { started.current = true; trackDemo(app, lang, "play"); }
  };
  useEffect(() => {
    started.current = false; mid.current = false; done.current = false;
    const id = setTimeout(ensurePlay, 1200);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, lang]);
  useEffect(() => {
    if (!total || total < 1) return;
    const frac = (stepIndex + 1) / total;
    if (frac >= 0.25 && !mid.current) { ensurePlay(); mid.current = true; trackDemo(app, lang, "midway"); }
    if (frac >= 0.75 && !done.current) { ensurePlay(); done.current = true; trackDemo(app, lang, "complete"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, lang, stepIndex, total]);
}
